import { useState, useEffect, useRef } from "react";
import { DefaultViewControl } from "@/components/DefaultViewControl";
import { useSearchParams } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { PageHeader } from "@/components/AppLayout";
import { Field } from "@/pages/Companies";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Plus, Truck, Trash2, X, Pencil, FileDown, Loader2, Mail, Eye, EyeOff, Paperclip, ShieldCheck, Undo2, Send, Check, CheckCheck, Lock } from "lucide-react";
import { openTimelineFile } from "@/components/AuthFile";
import { RecipientPicker } from "@/components/RecipientPicker";
import { AutoCcChips } from "@/components/AutoCcChips";
import { useAuth } from "@/lib/auth";
import { fmtDate, fmtDateTime, fmtCurrency, fmtCurrencyExact } from "@/lib/constants";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { ApprovalThread } from "@/components/ApprovalThread";
import { DatePicker } from "@/components/DatePicker";

const STATUS_COLOR = {
  draft: "border-slate-300 bg-slate-50 text-slate-700",
  pending_approval: "border-amber-300 bg-amber-50 text-amber-800",
  changes_requested: "border-rose-300 bg-rose-50 text-rose-800",
  approved: "border-cyan-300 bg-cyan-50 text-cyan-800",
  ordered: "border-violet-300 bg-violet-50 text-violet-800",
  delivered: "border-emerald-300 bg-emerald-50 text-emerald-800",
  cancelled: "border-slate-300 bg-slate-100 text-slate-500 line-through decoration-slate-400",
};

const PO_STEPS = ["draft", "pending_approval", "approved", "ordered", "delivered"];
const STEP_LABEL = { draft: "Draft", pending_approval: "Pending approval", approved: "Approved", ordered: "Ordered", delivered: "Delivered" };
const SENDABLE = ["approved", "ordered", "delivered"];
const LOCKED_STATUSES = ["pending_approval", "approved", "ordered", "delivered"];

const FILTER_STAGES = ["draft", "pending_approval", "changes_requested", "approved", "ordered", "delivered", "cancelled"];
const STAGE_FILTER_LABEL = { ...STEP_LABEL, changes_requested: "Changes requested", cancelled: "Cancelled" };
const SENT_PERIODS = [
  { v: "week", label: "Sent — last week" },
  { v: "month", label: "Sent — last month" },
  { v: "quarter", label: "Sent — last quarter" },
  { v: "ytd", label: "Sent — year to date" },
];
const sentSince = (p) => {
  if (p === "ytd") return new Date(new Date().getFullYear(), 0, 1);
  const days = { week: 7, month: 30, quarter: 92 }[p];
  return new Date(Date.now() - days * 86400000);
};

const EMPTY = { supplier_id: "none", job_id: "none", expected_delivery: "", status: "draft", lines: [], notes: "", delivery_address: "", delivery_contact: "", delivery_instructions: "", hide_prices: false };

const TRAIL_LABEL = { submitted: "Submitted for approval", resubmitted: "Resubmitted", changes_requested: "Changes requested", approved: "Approved", revised: "Revised" };
const TRAIL_DOT = { submitted: "bg-amber-400", resubmitted: "bg-amber-400", changes_requested: "bg-rose-500", approved: "bg-emerald-500", revised: "bg-slate-400" };

const isDelayed = (o) => !!(o?.expected_date && o?.expected_delivery && o.expected_date.slice(0, 10) > o.expected_delivery.slice(0, 10));

export default function Orders() {
  const qc = useQueryClient();
  const { user } = useAuth();
  const isApprover = !!(user?.po_approver || user?.role === "super_admin");
  const [editing, setEditing] = useState(null);
  const [params, setParams] = useSearchParams();
  const [open, setOpen] = useState(false);
  const [pdfBusy, setPdfBusy] = useState(null);
  const [emailPO, setEmailPO] = useState(null); // order being emailed
  const [emailForm, setEmailForm] = useState({ to_email: "", subject: "", message: "" });
  const [emailBusy, setEmailBusy] = useState(false);
  const [poRecipients, setPoRecipients] = useState([]);
  const [poSelected, setPoSelected] = useState([]);
  const [poTemplates, setPoTemplates] = useState([]);
  const [poTemplateId, setPoTemplateId] = useState("");
  const [poPreview, setPoPreview] = useState(null);
  const [poCc, setPoCc] = useState(null); // null = not loaded → backend applies defaults
  const [showPoPreview, setShowPoPreview] = useState(false);
  const [poPreviewLoading, setPoPreviewLoading] = useState(false);

  const openEmailPO = async (o) => {
    try {
      const { data } = await api.get(`/orders/${o.id}/email-prefill`);
      setEmailForm({ to_email: data.to_email || "", subject: data.subject || "", message: data.message || "" });
      setPoRecipients(data.recipient_options || []);
      setPoSelected((data.recipient_options || []).filter((r) => r.default).map((r) => r.email));
      setPoTemplates(data.templates || []);
      setPoTemplateId((data.templates || []).find((t) => t.default)?.id || "");
      setPoPreview(null); setShowPoPreview(false);
      setPoCc(null);
      api.get("/email/auto-cc").then(({ data }) => setPoCc(data.po_cc || [])).catch(() => setPoCc(null));
      setEmailPO(o);
      if (!(data.recipient_options || []).length) toast.warning("The supplier has no email on file — add one below");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not prepare the email");
    }
  };
  const applyPoTemplate = (id) => {
    const t = poTemplates.find((x) => x.id === id);
    if (!t) return;
    setPoTemplateId(id);
    setEmailForm((f) => ({ ...f, subject: t.subject || "", message: t.message || "" }));
  };
  const loadPoPreview = async () => {
    setPoPreviewLoading(true);
    try {
      const { data } = await api.post(`/orders/${emailPO.id}/email-preview`, {
        subject: emailForm.subject || undefined, message: emailForm.message || undefined,
      });
      setPoPreview(data); setShowPoPreview(true);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not build preview");
    } finally { setPoPreviewLoading(false); }
  };
  const sendEmailPO = async () => {
    setEmailBusy(true);
    try {
      const { data } = await api.post(`/orders/${emailPO.id}/email`, {
        to_emails: poSelected, subject: emailForm.subject, message: emailForm.message,
        cc_emails: poCc ? poCc.map((u) => u.email) : undefined,
      });
      if (data.status === "mocked") toast.warning("MOCK mode — email logged but not delivered (no email key set)");
      else if (data.status === "failed") toast.error(`Email failed: ${data.error || "delivery error"}`);
      else toast.success(emailPO?.status === "approved" ? `PO emailed to ${data.email} — marked as Ordered` : `PO emailed to ${data.email}`);
      qc.invalidateQueries({ queryKey: ["orders"] });
      setEmailPO(null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not send the PO");
    } finally {
      setEmailBusy(false);
    }
  };

  const { data: orders = [] } = useQuery({ queryKey: ["orders"], queryFn: async () => (await api.get("/orders")).data });
  const [stageFilter, setStageFilter] = useState("all");
  const [sentFilter, setSentFilter] = useState("all");
  const filtered = orders.filter((o) => {
    if (stageFilter !== "all" && o.status !== stageFilter) return false;
    if (sentFilter !== "all") {
      if (!o.po_emailed_at) return false;
      if (new Date(o.po_emailed_at) < sentSince(sentFilter)) return false;
    }
    return true;
  });
  const filtersActive = stageFilter !== "all" || sentFilter !== "all";

  const { data: unreadData } = useQuery({
    queryKey: ["orders-unread"],
    queryFn: async () => (await api.get("/orders/unread/summary")).data,
    refetchInterval: 60000,
  });
  const unreadMap = unreadData?.orders || {};
  const unreadTotal = unreadData?.total || 0;
  const markRead = useMutation({
    mutationFn: async (id) => api.post(`/orders/${id}/mark-read`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["orders-unread"] }),
  });
  const markAllRead = useMutation({
    mutationFn: async () => api.post("/orders/unread/mark-all"),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["orders-unread"] }); toast.success("All PO updates marked as read"); },
  });
  // Opening a PO auto-clears its unread badge.
  useEffect(() => {
    if (open && editing?.id && unreadMap[editing.id]) markRead.mutate(editing.id);
  }, [open, editing?.id, unreadMap]); // eslint-disable-line react-hooks/exhaustive-deps

  const expectedDate = useMutation({
    mutationFn: async ({ id, expected_date }) => (await api.patch(`/orders/${id}/expected-date`, { expected_date })).data,
    onSuccess: (data, vars) => {
      setEditing((prev) => (prev && prev.id === vars.id ? { ...prev, expected_date: vars.expected_date } : prev));
      qc.invalidateQueries({ queryKey: ["orders"] });
      qc.invalidateQueries({ queryKey: ["po-chat", vars.id] });
      toast.success("Expected date updated — logged in the PO updates");
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Could not update the expected date"),
  });
  const { data: suppliers = [] } = useQuery({ queryKey: ["suppliers-all-o"], queryFn: async () => (await api.get("/suppliers")).data });
  const { data: jobs = [] } = useQuery({ queryKey: ["jobs-all-o"], queryFn: async () => (await api.get("/jobs")).data });
  const { data: materials = [] } = useQuery({ queryKey: ["materials-all-o"], queryFn: async () => (await api.get("/materials")).data });
  const supMap = Object.fromEntries(suppliers.map((s) => [s.id, s.name]));
  const approvedSuppliers = suppliers.filter((s) => ["approved", "approved_legacy"].includes(s.approval_status) || s.temp_approval);
  const jobLabel = (j) => (j.job_number ? `${j.job_number} — ${j.title}` : j.title);
  const jobMap = Object.fromEntries(jobs.map((j) => [j.id, jobLabel(j)]));

  const save = useMutation({
    mutationFn: async (body) => {
      const payload = {
        ...body,
        supplier_id: body.supplier_id === "none" ? null : body.supplier_id,
        job_id: body.job_id === "none" ? null : body.job_id,
      };
      if (body.id) return (await api.patch(`/orders/${body.id}`, payload)).data;
      return (await api.post("/orders", payload)).data;
    },
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["orders"] }); setOpen(false); toast.success("Saved"); },
    onError: (e) => toast.error(e?.response?.data?.detail || "Could not save the PO"),
  });
  const advance = useMutation({
    mutationFn: async ({ id, status }) => (await api.patch(`/orders/${id}`, { status })).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["orders"] }),
  });
  const del = useMutation({
    mutationFn: async (id) => (await api.delete(`/orders/${id}`)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["orders"] }),
    onError: (e) => toast.error(e?.response?.data?.detail || "Could not delete the PO"),
  });
  const confirmDelete = (o) => {
    if (!window.confirm(`Delete ${o.order_number}${LOCKED_STATUSES.includes(o.status) ? ` (${(o.status || "").replace("_", " ")})` : ""}? This can't be undone.`)) return;
    del.mutate(o.id);
  };
  const wf = useMutation({
    mutationFn: async ({ id, action, body, savePayload }) => {
      if (savePayload) await api.patch(`/orders/${id}`, savePayload);
      return (await api.post(`/orders/${id}/${action}`, body)).data;
    },
    onSuccess: (data, vars) => {
      qc.invalidateQueries({ queryKey: ["orders"] });
      setOpen(false); setPbPO(null); setRsPO(null);
      const msg = {
        "submit-approval": vars.resubmit ? "PO resubmitted for approval" : "PO submitted for approval — the approver has been notified",
        approve: "PO approved — PDF and Email are now unlocked",
        revise: data.status === "draft" && data.revision ? "PO unlocked for editing" : "Done",
        "request-changes": "Sent back for changes — the submitter can now edit and resubmit",
      };
      toast.success(msg[vars.action] || "Done");
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Could not update the PO"),
  });
  const [pbPO, setPbPO] = useState(null);
  const [pbComment, setPbComment] = useState("");
  const [rsPO, setRsPO] = useState(null);
  const [rsNote, setRsNote] = useState("");

  const addLine = () => setEditing({ ...editing, lines: [...(editing.lines || []), { _key: crypto.randomUUID(), name: "New item", qty: 1, unit: "ea", unit_price: 0, required_by: editing.expected_delivery || "" }] });
  const updLine = (idx, patch) => {
    const lines = [...editing.lines];
    lines[idx] = { ...lines[idx], ...patch };
    setEditing({ ...editing, lines });
  };
  const rmLine = (idx) => setEditing({ ...editing, lines: editing.lines.filter((_, i) => i !== idx) });
  const pickMaterial = (idx, materialId) => {
    const m = materials.find((x) => x.id === materialId);
    if (!m) return;
    updLine(idx, { material_id: m.id, name: m.name, unit: m.unit, unit_price: m.unit_price });
  };

  const orderTotal = (editing?.lines || []).reduce((s, l) => s + (l.qty || 0) * (l.unit_price || 0), 0);

  const openEdit = (o) => {
    setEditing({ ...EMPTY, ...o, supplier_id: o.supplier_id || "none", job_id: o.job_id || "none" });
    setOpen(true);
  };

  // Deep link from the Approvals hub: /orders?open={poId} opens the PO view dialog directly.
  useEffect(() => {
    const openId = params.get("open");
    if (openId && orders.length) {
      const o = orders.find((x) => x.id === openId);
      if (o) openEdit(o);
      params.delete("open");
      setParams(params, { replace: true });
    }
  }, [params, orders]); // eslint-disable-line react-hooks/exhaustive-deps

  const downloadPdf = async (o) => {
    setPdfBusy(o.id);
    try {
      const res = await api.get(`/orders/${o.id}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `${o.order_number || "purchase-order"}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success(`${o.order_number} PDF downloaded — ready to email to the supplier`);
    } catch {
      toast.error("Could not generate the PO PDF");
    }
    setPdfBusy(null);
  };
  const linkJob = (v) => {
    const j = jobs.find((x) => x.id === v);
    setEditing((prev) => ({
      ...prev,
      job_id: v,
      delivery_address: prev.delivery_address || (j?.site_address || ""),
    }));
  };

  return (
    <div>
      <PageHeader
        title="Purchase Orders"
        subtitle="Purchase orders to suppliers — link to jobs, track delivery, build receiving notes."
        breadcrumb={["Operations", "Purchase Orders"]}
        actions={
          <Button data-testid="new-order-btn" onClick={() => { setEditing({ ...EMPTY }); setOpen(true); }} className="rounded-sm h-10 bg-cyan-600 hover:bg-cyan-700 text-white">
            <Plus size={16} className="mr-2" /> New PO
          </Button>
        }
      />
      <div className="px-6 lg:px-10 py-6">
        <div className="flex flex-wrap items-center gap-2 mb-4" data-testid="orders-filter-bar">
          <Select value={stageFilter} onValueChange={setStageFilter}>
            <SelectTrigger className="rounded-sm h-9 w-[180px] text-sm" data-testid="orders-stage-filter">
              <SelectValue placeholder="All stages" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All stages</SelectItem>
              {FILTER_STAGES.map((s) => <SelectItem key={s} value={s} data-testid={`orders-stage-opt-${s}`}>{STAGE_FILTER_LABEL[s]}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={sentFilter} onValueChange={setSentFilter}>
            <SelectTrigger className="rounded-sm h-9 w-[190px] text-sm" data-testid="orders-sent-filter">
              <SelectValue placeholder="Sent — any time" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Sent — any time</SelectItem>
              {SENT_PERIODS.map((p) => <SelectItem key={p.v} value={p.v} data-testid={`orders-sent-opt-${p.v}`}>{p.label}</SelectItem>)}
            </SelectContent>
          </Select>
          {filtersActive && (
            <Button size="sm" variant="ghost" data-testid="orders-clear-filters"
              onClick={() => { setStageFilter("all"); setSentFilter("all"); }}
              className="h-9 rounded-sm text-xs text-slate-500">
              <X size={13} className="mr-1" /> Clear
            </Button>
          )}
          <DefaultViewControl screen="orders"
            current={{ stage: stageFilter, sent: sentFilter }}
            onApply={(f) => { if (f.stage) setStageFilter(f.stage); if (f.sent) setSentFilter(f.sent); }} />
          {unreadTotal > 0 && (
            <div className="flex items-center gap-2">
              <span data-testid="orders-unread-total" className="text-xs font-medium text-amber-700 bg-amber-50 border border-amber-200 rounded-sm px-2 py-1.5 whitespace-nowrap">
                {unreadTotal} unread update{unreadTotal === 1 ? "" : "s"}
              </span>
              <Button size="sm" variant="outline" data-testid="orders-mark-all-read" onClick={() => markAllRead.mutate()}
                disabled={markAllRead.isPending} className="h-9 rounded-sm text-xs whitespace-nowrap">
                <CheckCheck size={14} className="mr-1.5" /> Mark all read
              </Button>
            </div>
          )}
          <span className="text-xs text-slate-400 ml-auto" data-testid="orders-filter-count">
            {filtered.length} of {orders.length} POs{sentFilter !== "all" ? " · filtered by emailed date" : ""}
          </span>
        </div>
        <Card className="rounded-sm border-slate-200 industrial-shadow overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr className="text-left stamp-label text-slate-500">
                <th className="px-4 py-3">PO #</th>
                <th className="px-4 py-3">Supplier</th>
                <th className="px-4 py-3 hidden md:table-cell">Linked job</th>
                <th className="px-4 py-3">Lines</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3 hidden lg:table-cell">Expected</th>
                <th className="px-4 py-3 text-right">Total</th>
                <th className="px-4 py-3 w-32"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {filtered.map((o) => (
                <tr key={o.id} className={`hover:bg-cyan-50/30 ${unreadMap[o.id] ? "bg-sky-50/60" : ""}`} data-testid={`order-row-${o.id}`}>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      {unreadMap[o.id] ? <span className="w-2 h-2 rounded-full bg-sky-500 shrink-0" data-testid={`order-unread-dot-${o.id}`} /> : <Truck size={14} className="text-slate-400" />}
                      <span className="font-mono text-xs font-semibold">{o.order_number}</span>
                      {unreadMap[o.id] && (
                        <Badge data-testid={`order-unread-pill-${o.id}`} className="rounded-full text-[9px] bg-amber-400 text-slate-900 border-0 font-bold hover:bg-amber-400">{unreadMap[o.id]} new</Badge>
                      )}
                      {(o.revision || 1) >= 2 && (
                        <Badge variant="outline" className="rounded-sm text-[9px] px-1 border-amber-300 bg-amber-100 text-amber-800 font-bold" data-testid={`order-rev-${o.id}`}>Rev {o.revision}</Badge>
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-slate-700">{supMap[o.supplier_id] || "—"}</td>
                  <td className="px-4 py-3 text-slate-500 hidden md:table-cell text-xs">{jobMap[o.job_id] || "—"}</td>
                  <td className="px-4 py-3 text-slate-600 text-xs">{o.lines?.length || 0}</td>
                  <td className="px-4 py-3"><Badge variant="outline" className={`rounded-sm text-[10px] ${STATUS_COLOR[o.status]}`} data-testid={`order-stage-${o.id}`}>{STAGE_FILTER_LABEL[o.status] || (o.status || "").replace("_", " ")}</Badge></td>
                  <td className="px-4 py-3 hidden lg:table-cell text-xs">
                    {isDelayed(o) ? (
                      <span className="inline-flex items-center gap-1.5">
                        <span className="text-rose-600 font-semibold">{fmtDate(o.expected_date)}</span>
                        <Badge data-testid={`order-delayed-${o.id}`} className="rounded-sm text-[8px] px-1 bg-rose-600 text-white border-0 font-bold hover:bg-rose-600">DELAYED</Badge>
                      </span>
                    ) : (
                      <span className="text-slate-500">{fmtDate(o.expected_date || o.expected_delivery)}</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-right font-mono font-semibold text-slate-900">{fmtCurrency(o.total)}</td>
                  <td className="px-4 py-3">
                    <div className="flex gap-1 items-center">
                      {SENDABLE.includes(o.status) && (
                        <Button size="sm" variant="ghost" data-testid={`order-pdf-${o.id}`} title="Download PO PDF"
                          disabled={pdfBusy === o.id}
                          onClick={() => downloadPdf(o)} className="h-7 w-7 p-0 text-slate-400 hover:text-cyan-600">
                          {pdfBusy === o.id ? <Loader2 size={12} className="animate-spin" /> : <FileDown size={12} />}
                        </Button>
                      )}
                      {SENDABLE.includes(o.status) && (
                        <Button size="sm" variant="ghost" data-testid={`order-email-${o.id}`} title={o.po_emailed_at ? `Emailed to ${o.po_emailed_to} — send again` : "Email PO to supplier"}
                          onClick={() => openEmailPO(o)} className={`h-7 w-7 p-0 ${o.po_emailed_at ? "text-emerald-600" : "text-slate-400"} hover:text-emerald-700`}>
                          <Mail size={12} />
                        </Button>
                      )}
                      <Button size="sm" variant="ghost" data-testid={`order-edit-${o.id}`} title={["draft", "changes_requested"].includes(o.status) ? "Edit PO" : "View PO"}
                        onClick={() => openEdit(o)} className="h-7 w-7 p-0 text-slate-400 hover:text-cyan-600">
                        {["draft", "changes_requested"].includes(o.status) ? <Pencil size={12} /> : <Eye size={12} />}
                      </Button>
                      {unreadMap[o.id] && (
                        <Button size="sm" variant="ghost" className="h-7 px-2 text-emerald-700 text-[10px]" data-testid={`order-mark-read-${o.id}`}
                          onClick={() => markRead.mutate(o.id)} title="Mark as read"><Check size={11} className="mr-0.5" />Read</Button>
                      )}
                      {o.status === "draft" && (
                        <Button size="sm" data-testid={`order-submit-${o.id}`} disabled={wf.isPending}
                          onClick={() => {
                            if (!(o.lines || []).length) { toast.error(`${o.order_number} has no order lines — open the PO and add at least one line first`); return; }
                            if (!o.supplier_id) { toast.error(`${o.order_number} has no supplier — open the PO and pick one first`); return; }
                            setRsNote(""); setRsPO(o);
                          }}
                          title={!(o.lines || []).length ? "Add at least one order line first" : !o.supplier_id ? "Pick a supplier first" : "Submit for approval"}
                          className={`h-7 rounded-sm text-[10px] bg-cyan-600 hover:bg-cyan-700 ${(o.lines || []).length && o.supplier_id ? "" : "opacity-40"}`}>Submit</Button>
                      )}
                      {o.status === "pending_approval" && isApprover && (
                        <>
                          <Button size="sm" data-testid={`order-approve-${o.id}`} disabled={wf.isPending}
                            onClick={() => wf.mutate({ id: o.id, action: "approve" })}
                            className="h-7 rounded-sm text-[10px] bg-emerald-600 hover:bg-emerald-700"><ShieldCheck size={11} className="mr-1" />Approve</Button>
                          <Button size="sm" data-testid={`order-request-changes-${o.id}`} disabled={wf.isPending}
                            onClick={() => { setPbComment(""); setPbPO(o); }}
                            className="h-7 rounded-sm text-[10px] bg-rose-600 hover:bg-rose-700 text-white">Changes</Button>
                        </>
                      )}
                      {o.status === "pending_approval" && !isApprover && (
                        <Button size="sm" disabled data-testid={`order-pending-${o.id}`}
                          className="h-7 rounded-sm text-[10px] bg-slate-200 text-slate-400">Pending approval</Button>
                      )}
                      {o.status === "changes_requested" && (
                        <Button size="sm" data-testid={`order-resubmit-${o.id}`} disabled={wf.isPending}
                          onClick={() => { setRsNote(""); setRsPO(o); }}
                          className="h-7 rounded-sm text-[10px] bg-cyan-600 hover:bg-cyan-700">Resubmit</Button>
                      )}
                      {o.status === "approved" && (
                        <Button size="sm" data-testid={`order-send-${o.id}`}
                          onClick={() => openEmailPO(o)}
                          className="h-7 rounded-sm text-[10px] bg-cyan-600 hover:bg-cyan-700"><Send size={11} className="mr-1" />Send</Button>
                      )}
                      {["pending_approval", "approved"].includes(o.status) && (
                        <Button size="sm" variant="outline" data-testid={`order-revise-${o.id}`} disabled={wf.isPending}
                          onClick={() => wf.mutate({ id: o.id, action: "revise" })}
                          className="h-7 rounded-sm text-[10px] border-slate-300 text-slate-600"><Undo2 size={11} className="mr-1" />Revise</Button>
                      )}
                      {o.status === "ordered" && (
                        <Button size="sm" data-testid={`order-receive-${o.id}`}
                          onClick={() => advance.mutate({ id: o.id, status: "delivered" })}
                          className="h-7 rounded-sm text-[10px] bg-emerald-600 hover:bg-emerald-700">Receive</Button>
                      )}
                      <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-rose-600" data-testid={`order-delete-${o.id}`} onClick={() => confirmDelete(o)}><Trash2 size={12} /></Button>
                    </div>
                  </td>
                </tr>
              ))}
              {orders.length === 0 && <tr><td colSpan={8} className="px-4 py-12 text-center text-slate-400">No purchase orders</td></tr>}
              {orders.length > 0 && filtered.length === 0 && (
                <tr><td colSpan={8} className="px-4 py-12 text-center text-slate-400" data-testid="orders-no-match">
                  No POs match these filters. <button className="text-cyan-600 font-semibold hover:underline" onClick={() => { setStageFilter("all"); setSentFilter("all"); }}>Clear filters</button>
                </td></tr>
              )}
            </tbody>
          </table>
        </Card>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-sm sm:max-w-3xl max-h-[92vh] overflow-y-auto">
          {(() => {
            const locked = !!editing?.id && LOCKED_STATUSES.includes(editing?.status);
            const isCR = editing?.status === "changes_requested";
            const stepIdx = PO_STEPS.indexOf(isCR ? "pending_approval" : editing?.status);
            return editing && (
              <>
                <DialogHeader>
                  <DialogTitle className="font-display text-xl flex items-center gap-2">
                    {editing?.id ? (locked ? `View PO ${editing.order_number || ""}` : "Edit PO") : "New purchase order"}
                    {(editing?.revision || 1) >= 2 && (
                      <Badge variant="outline" className="rounded-sm text-[10px] border-amber-300 bg-amber-100 text-amber-800 font-bold">Rev {editing.revision}</Badge>
                    )}
                  </DialogTitle>
                </DialogHeader>

                {editing?.id && (PO_STEPS.includes(editing.status) || isCR) && (
                  <div className="flex items-center flex-wrap gap-y-1 text-[10px] font-semibold" data-testid="po-workflow-stepper">
                    {PO_STEPS.map((s, i) => {
                      const cr = isCR && i === stepIdx;
                      return (
                      <div key={s} className="flex items-center">
                        {i > 0 && <div className={`w-6 h-px mx-1.5 ${i <= stepIdx ? "bg-emerald-400" : "bg-slate-200"}`} />}
                        <span className={`w-5 h-5 rounded-full flex items-center justify-center text-[9px] mr-1
                          ${i < stepIdx ? "bg-emerald-600 text-white" : i === stepIdx ? (cr ? "bg-rose-500 text-white" : s === "pending_approval" ? "bg-amber-500 text-white" : "bg-cyan-600 text-white") : "bg-slate-200 text-slate-500"}`}>
                          {i < stepIdx ? "✓" : cr ? "!" : i + 1}
                        </span>
                        <span className={i === stepIdx ? (cr ? "text-rose-700" : s === "pending_approval" ? "text-amber-700" : "text-cyan-700") : i < stepIdx ? "text-slate-600" : "text-slate-400"}>
                          {cr ? "Changes requested" : STEP_LABEL[s]}
                        </span>
                      </div>
                      );
                    })}
                  </div>
                )}

                {editing?.id && editing.status === "pending_approval" && (
                  <div className="border-l-4 border-amber-400 bg-amber-50 px-3 py-2.5 rounded-sm flex items-start justify-between gap-3" data-testid="po-pending-banner">
                    <div className="text-xs text-amber-900 leading-snug">
                      <b>Locked — Rev {editing.revision || 1} · awaiting approval.</b> Lines, supplier and delivery details can't be edited while the PO is pending.
                      {editing.submitted_for_approval_by_name && <> Submitted by <b>{editing.submitted_for_approval_by_name}</b>{editing.submitted_for_approval_at ? ` · ${fmtDate(editing.submitted_for_approval_at)}` : ""}.</>}
                      {(() => {
                        const ev = [...(editing.approval_trail || [])].reverse().find((t) => t.event === "submitted" || t.event === "resubmitted");
                        return ev?.note ? (
                          <div className="mt-1.5 bg-white border border-amber-200 rounded-sm px-2.5 py-1.5 text-slate-700 italic flex items-start gap-2" data-testid="po-submitter-note">
                            <span className="not-italic stamp-label text-amber-700 shrink-0 pt-px">Submitter's note</span>
                            <span>“{ev.note}”</span>
                          </div>
                        ) : null;
                      })()}
                    </div>
                    <div className="flex gap-1.5 shrink-0">
                      {isApprover ? (
                        <>
                          <Button size="sm" data-testid="po-dialog-approve-btn" disabled={wf.isPending}
                            onClick={() => wf.mutate({ id: editing.id, action: "approve" })}
                            className="h-8 rounded-sm text-[11px] bg-emerald-600 hover:bg-emerald-700"><ShieldCheck size={12} className="mr-1" />Approve PO</Button>
                          <Button size="sm" data-testid="po-dialog-request-changes-btn" disabled={wf.isPending}
                            onClick={() => { setPbComment(""); setPbPO(editing); }}
                            className="h-8 rounded-sm text-[11px] bg-rose-600 hover:bg-rose-700 text-white">Request changes</Button>
                        </>
                      ) : (
                        <Button size="sm" disabled className="h-8 rounded-sm text-[11px] bg-slate-200 text-slate-400">Pending approval</Button>
                      )}
                      <Button size="sm" variant="outline" data-testid="po-dialog-revise-btn" disabled={wf.isPending}
                        onClick={() => wf.mutate({ id: editing.id, action: "revise" })}
                        className="h-8 rounded-sm text-[11px] border-slate-300 text-slate-700"><Undo2 size={12} className="mr-1" />Revise</Button>
                    </div>
                  </div>
                )}

                {editing?.id && (editing.status === "pending_approval" || (editing.approval_thread || []).length > 0) && (
                  <ApprovalThread kind="po" itemId={editing.id} thread={editing.approval_thread || []}
                    canReply={editing.status === "pending_approval" && (isApprover || user?.email === editing.submitted_for_approval_by)}
                    onPosted={(msg) => setEditing((e) => ({ ...e, approval_thread: [...(e.approval_thread || []), msg] }))} />
                )}

                {editing?.id && editing.status === "changes_requested" && (
                  <div className="border-l-4 border-rose-500 bg-rose-50 px-3 py-2.5 rounded-sm space-y-1.5" data-testid="po-changes-banner">
                    <div className="flex items-start justify-between gap-3">
                      <div className="text-xs text-rose-900 leading-snug">
                        <b>Changes requested{editing.changes_requested_by_name ? ` by ${editing.changes_requested_by_name}` : ""}</b>
                        {editing.changes_requested_at ? ` · ${fmtDate(editing.changes_requested_at)}` : ""}
                        {editing.changes_requested_comment && (
                          <div className="mt-1 bg-white border border-rose-200 rounded-sm px-2 py-1.5 text-slate-700 italic">“{editing.changes_requested_comment}”</div>
                        )}
                      </div>
                      <Button size="sm" data-testid="po-dialog-resubmit-btn" disabled={wf.isPending}
                        onClick={() => { setRsNote(""); setRsPO(editing); }}
                        className="h-8 rounded-sm text-[11px] bg-cyan-600 hover:bg-cyan-700 shrink-0">Resubmit for approval</Button>
                    </div>
                    <div className="text-[10px] text-rose-700">Editing is unlocked — make the changes and resubmit. The revision number is not changed by this.</div>
                  </div>
                )}

                {editing?.id && SENDABLE.includes(editing.status) && (
                  <div className="border-l-4 border-emerald-500 bg-emerald-50 px-3 py-2.5 rounded-sm flex items-start justify-between gap-3" data-testid="po-approved-banner">
                    <div className="text-xs text-emerald-900 leading-snug">
                      <b>Approved{editing.po_approved_by_name ? ` by ${editing.po_approved_by_name}` : ""}</b>{editing.po_approved_at ? ` · ${fmtDate(editing.po_approved_at)}` : ""}
                      {editing.status === "approved" ? <> — PDF download and Email to supplier are unlocked.</> : editing.status === "ordered" ? <> — sent to the supplier. Track ETA updates in the dates panel and chat below.</> : <> — delivered.</>}
                      {editing.status !== "delivered" && <> Editing requires <b>Revise</b> (new revision, restarts approval).</>}
                    </div>
                    {editing.status !== "delivered" && (
                      <div className="flex gap-1.5 shrink-0">
                        {editing.status === "approved" && (
                          <Button size="sm" data-testid="po-dialog-send-btn"
                            onClick={() => { setOpen(false); openEmailPO(editing); }}
                            className="h-8 rounded-sm text-[11px] bg-cyan-600 hover:bg-cyan-700"><Send size={12} className="mr-1" />Send to supplier</Button>
                        )}
                        {editing.status !== "delivered" && (
                          <Button size="sm" variant="outline" data-testid="po-dialog-revise-approved-btn" disabled={wf.isPending}
                            onClick={() => wf.mutate({ id: editing.id, action: "revise" })}
                            className="h-8 rounded-sm text-[11px] border-slate-300 text-slate-700"><Undo2 size={12} className="mr-1" />Revise</Button>
                        )}
                      </div>
                    )}
                  </div>
                )}

                {editing?.id && (editing.approval_trail || []).length > 0 && (
                  <div className="border border-slate-200 rounded-sm px-3 py-2" data-testid="po-approval-trail">
                    <div className="stamp-label text-slate-500 mb-1.5">Approval trail</div>
                    <div className="space-y-1 max-h-28 overflow-y-auto text-[11px]">
                      {[...editing.approval_trail].reverse().map((t, i) => (
                        <div key={i} className="flex gap-2 items-baseline">
                          <span className="font-mono text-slate-400 shrink-0 w-32">{fmtDateTime(t.at)}</span>
                          <span className={`w-1.5 h-1.5 rounded-full shrink-0 translate-y-px ${TRAIL_DOT[t.event] || "bg-slate-300"}`} />
                          <span className="text-slate-700"><b>{TRAIL_LABEL[t.event] || t.event}</b> by {t.by_name || t.by}{t.note ? <span className="text-slate-500"> — “{t.note}”</span> : null}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                <fieldset disabled={locked} className={locked ? "opacity-70" : ""} data-testid="po-dialog-fields">
                  <div className="space-y-4 max-h-[60vh] overflow-y-auto pr-2">
              <div className="grid grid-cols-3 gap-3">
                <Field label="Supplier" required>
                  <Select value={editing.supplier_id} onValueChange={(v) => setEditing({ ...editing, supplier_id: v })}>
                    <SelectTrigger data-testid="order-supplier-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">— Select —</SelectItem>
                      {approvedSuppliers.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}{s.approval_status === "approved_legacy" ? " (legacy)" : !["approved", "approved_legacy"].includes(s.approval_status) && s.temp_approval ? " (temp approved)" : ""}</SelectItem>)}
                      {editing.supplier_id && editing.supplier_id !== "none" && !approvedSuppliers.some((s) => s.id === editing.supplier_id) && (
                        <SelectItem value={editing.supplier_id} disabled>{supMap[editing.supplier_id] || "Supplier"} — not approved</SelectItem>
                      )}
                    </SelectContent>
                  </Select>
                  <p className="text-[10px] text-slate-400 mt-1">Approved suppliers only — onboard new ones from the Suppliers page.</p>
                </Field>
                <Field label="Link to job">
                  <Select value={editing.job_id} onValueChange={linkJob}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">— None —</SelectItem>
                      {jobs.map((j) => <SelectItem key={j.id} value={j.id}>{jobLabel(j)}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </Field>
                <Field label="Requested delivery">
                  <DatePicker testId="order-expected-delivery" value={editing.expected_delivery ? editing.expected_delivery.slice(0,10) : ""} onChange={(v) => setEditing({ ...editing, expected_delivery: v })} placeholder="Pick a delivery date" />
                  <p className="text-[10px] text-slate-400 mt-1">Locks when the PO is sent to the supplier.</p>
                </Field>
              </div>
              <div className="border border-slate-200 p-3">
                <div className="stamp-label text-slate-600 mb-2">Delivery</div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="col-span-2">
                    <Field label="Delivery address">
                      <Input data-testid="order-delivery-address" value={editing.delivery_address || ""} onChange={(e) => setEditing({ ...editing, delivery_address: e.target.value })} placeholder="Site address or workshop — prefills from the linked job" />
                    </Field>
                  </div>
                  <Field label="On-site contact">
                    <Input data-testid="order-delivery-contact" value={editing.delivery_contact || ""} onChange={(e) => setEditing({ ...editing, delivery_contact: e.target.value })} placeholder="e.g. Daniel · 0400 111 222" />
                  </Field>
                  <Field label="Delivery instructions">
                    <Input data-testid="order-delivery-instructions" value={editing.delivery_instructions || ""} onChange={(e) => setEditing({ ...editing, delivery_instructions: e.target.value })} placeholder="e.g. Deliver 6:45–7:15 AM, call on approach" />
                  </Field>
                </div>
              </div>
              <div>
                <div className="flex items-center justify-between mb-2">
                  <div className="stamp-label text-slate-600">Order lines</div>
                  {!locked && <Button size="sm" onClick={addLine} variant="outline" className="rounded-sm h-7"><Plus size={12} className="mr-1" /> Line</Button>}
                </div>
                <div className="border border-slate-200">
                  <div className="grid grid-cols-[1fr_60px_50px_115px_80px_80px_26px] gap-1.5 px-2 pt-2 text-[9px] stamp-label text-slate-400">
                    <div>Item</div><div className="text-right">Qty</div><div>Unit</div><div>Required by</div><div className="text-right">Unit price</div><div className="text-right">Total</div><div />
                  </div>
                  {(editing.lines || []).map((l, idx) => (
                    <div key={l._key || l.material_id || `line-${idx}`} className="grid grid-cols-[1fr_60px_50px_115px_80px_80px_26px] gap-1.5 p-2 border-b border-slate-100 last:border-b-0 items-center">
                      <div>
                        <Select value={l.material_id || "custom"} onValueChange={(v) => v === "custom" ? updLine(idx, { material_id: null }) : pickMaterial(idx, v)}>
                          <SelectTrigger className="h-8 rounded-sm text-xs"><SelectValue placeholder="Pick" /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="custom">— Custom —</SelectItem>
                            {materials.map((m) => <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>)}
                          </SelectContent>
                        </Select>
                        <Input value={l.name} onChange={(e) => updLine(idx, { name: e.target.value })} className="h-7 rounded-sm text-xs mt-1" />
                      </div>
                      <Input type="number" value={l.qty} onChange={(e) => updLine(idx, { qty: parseFloat(e.target.value) || 0 })} className="h-8 rounded-sm text-xs text-right" />
                      <Input value={l.unit} onChange={(e) => updLine(idx, { unit: e.target.value })} className="h-8 rounded-sm text-xs" />
                      <DatePicker testId={`line-required-by-${idx}`} value={l.required_by ? l.required_by.slice(0,10) : ""} onChange={(v) => updLine(idx, { required_by: v })} className="h-8 text-[11px] px-2" placeholder="Required by" />
                      <Input type="number" step="0.01" value={l.unit_price} onChange={(e) => updLine(idx, { unit_price: parseFloat(e.target.value) || 0 })} className="h-8 rounded-sm text-xs text-right font-mono" />
                      <div className="text-right font-mono text-xs font-semibold">{fmtCurrencyExact((l.qty || 0) * (l.unit_price || 0))}</div>
                      {!locked ? <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-rose-600" onClick={() => rmLine(idx)}><X size={12} /></Button> : <span />}
                    </div>
                  ))}
                  {(!editing.lines || editing.lines.length === 0) && <div className="p-6 text-center text-xs text-slate-400">No lines yet</div>}
                </div>
                <div className="mt-2 flex items-center justify-between">
                  <label className="flex items-center gap-2 text-xs text-slate-600 cursor-pointer select-none">
                    <Checkbox data-testid="order-hide-prices" checked={!!editing.hide_prices} onCheckedChange={(v) => setEditing({ ...editing, hide_prices: !!v })} />
                    <span>Hide prices on the supplier PDF (qty-only PO)</span>
                  </label>
                  <div className="font-mono text-sm font-bold text-slate-900">Total: {fmtCurrencyExact(orderTotal)}</div>
                </div>
                <Field label="Notes (shown on the PO)">
                  <Textarea data-testid="order-notes" rows={2} value={editing.notes || ""} onChange={(e) => setEditing({ ...editing, notes: e.target.value })} placeholder="Optional — anything else the supplier should know" />
                </Field>
              </div>
                  </div>
                </fieldset>
                {editing?.id && ["ordered", "delivered"].includes(editing.status) && (
                  <div className="border border-slate-200 rounded-sm p-3 mt-3" data-testid="po-dates-panel">
                    <div className="stamp-label text-slate-600 mb-2">Delivery dates</div>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <div className="text-[10px] stamp-label text-slate-400 flex items-center gap-1"><Lock size={9} /> Requested — locked on send</div>
                        <div className="text-sm font-semibold text-slate-800 mt-1.5" data-testid="po-requested-date">{editing.expected_delivery ? fmtDate(editing.expected_delivery) : "Not set"}</div>
                      </div>
                      <div>
                        <div className="text-[10px] stamp-label text-slate-400">Expected — supplier ETA</div>
                        <div className="flex items-center gap-2 mt-1">
                          <DatePicker testId="po-expected-date" value={editing.expected_date ? editing.expected_date.slice(0, 10) : ""}
                            onChange={(v) => expectedDate.mutate({ id: editing.id, expected_date: v || null })} placeholder="Supplier ETA" />
                          {isDelayed(editing) && <Badge data-testid="po-delayed-badge" className="rounded-sm text-[9px] bg-rose-600 text-white border-0 font-bold hover:bg-rose-600">DELAYED</Badge>}
                        </div>
                      </div>
                    </div>
                    {isDelayed(editing) && (
                      <p className="text-[11px] text-rose-600 mt-2" data-testid="po-delayed-note">Supplier ETA is past the requested delivery date — check the job schedule.</p>
                    )}
                  </div>
                )}
                {editing?.id && (
                  <div className="mt-3" data-testid="po-chat-section">
                    <div className="stamp-label text-slate-600 mb-2">Updates &amp; chat</div>
                    <POChat orderId={editing.id} />
                  </div>
                )}
                <DialogFooter>
                  <Button variant="outline" onClick={() => setOpen(false)} className="rounded-sm">{locked ? "Close" : "Cancel"}</Button>
                  {!locked && <Button data-testid="save-order-btn" onClick={() => save.mutate(editing)} className="rounded-sm bg-cyan-600 hover:bg-cyan-700">Save PO</Button>}
                  {!locked && editing?.id && editing.status === "draft" && (() => {
                    const hasLines = (editing.lines || []).filter((l) => (l.name || "").trim()).length > 0;
                    const hasSupplier = !!editing.supplier_id && editing.supplier_id !== "none";
                    return (
                      <Button data-testid="po-dialog-submit-btn" disabled={wf.isPending}
                        onClick={() => {
                          if (!hasLines) { toast.error("Add at least one order line before submitting for approval"); return; }
                          if (!hasSupplier) { toast.error("Pick a supplier before submitting for approval"); return; }
                          setRsNote(""); setRsPO(editing);
                        }}
                        title={!hasLines ? "Add at least one order line first" : !hasSupplier ? "Pick a supplier first" : "Submit for approval"}
                        className={`rounded-sm bg-emerald-600 hover:bg-emerald-700 text-white ${hasLines && hasSupplier ? "" : "opacity-40"}`}>
                        <ShieldCheck size={14} className="mr-1.5" /> Submit for approval
                      </Button>
                    );
                  })()}
                </DialogFooter>
              </>
            );
          })()}
        </DialogContent>
      </Dialog>

      {/* Email PO dialog (Trent, Jun 2026) */}
      <Dialog open={!!emailPO} onOpenChange={(o) => !o && !emailBusy && setEmailPO(null)}>
        <DialogContent className={`rounded-sm max-h-[90vh] overflow-y-auto ${showPoPreview ? "sm:max-w-3xl" : "sm:max-w-lg"}`} data-testid="email-po-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-xl flex items-center gap-2">
              <Mail size={18} className="text-emerald-600" /> Email {emailPO?.order_number || "PO"} to supplier
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <Field label="To" required>
              <div data-testid="email-po-to">
                <RecipientPicker options={poRecipients} selected={poSelected}
                  onChange={setPoSelected}
                  onAddCustom={(o) => setPoRecipients((prev) => [...prev, o])}
                  testPrefix="po-recipient" />
              </div>
            </Field>
            <AutoCcChips cc={poCc} testPrefix="po-cc" toEmails={poSelected}
              onRemove={(email) => setPoCc((prev) => (prev || []).filter((u) => u.email !== email))} />
            {poTemplates.length > 0 && (
              <Field label="Template">
                <div>
                  <Select value={poTemplateId} onValueChange={applyPoTemplate}>
                    <SelectTrigger className="rounded-sm" data-testid="po-template-select"><SelectValue placeholder="Choose a template…" /></SelectTrigger>
                    <SelectContent>
                      {poTemplates.map((t) => (
                        <SelectItem key={t.id} value={t.id} data-testid={`po-template-opt-${t.id}`}>{t.name}{t.default ? " (standard)" : ""}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <div className="text-[10px] text-slate-400 mt-1">Picking a template fills the subject and message — both stay editable. Admins manage templates under System → Email.</div>
                </div>
              </Field>
            )}
            <Field label="Subject">
              <Input data-testid="email-po-subject" value={emailForm.subject}
                onChange={(e) => setEmailForm({ ...emailForm, subject: e.target.value })} />
            </Field>
            <Field label="Message">
              <Textarea rows={6} data-testid="email-po-message" value={emailForm.message}
                onChange={(e) => setEmailForm({ ...emailForm, message: e.target.value })} className="rounded-sm text-sm" />
            </Field>
            <div className="flex items-center justify-between gap-2">
              <Button type="button" variant="outline" size="sm" disabled={poPreviewLoading}
                onClick={async () => { if (!showPoPreview) await loadPoPreview(); else setShowPoPreview(false); }}
                className="rounded-sm h-9" data-testid="email-po-preview-btn">
                {poPreviewLoading ? <Loader2 size={13} className="animate-spin mr-1.5" />
                  : showPoPreview ? <EyeOff size={13} className="mr-1.5" /> : <Eye size={13} className="mr-1.5" />}
                {poPreviewLoading ? "Loading preview…" : showPoPreview ? "Hide message preview" : "Preview message"}
              </Button>
              {poPreview?.attachments?.length > 0 && (
                <div className="flex flex-wrap gap-1" data-testid="email-po-attachments">
                  {poPreview.attachments.map((a, i) => (
                    <span key={i} className="inline-flex items-center gap-1 bg-white border border-slate-200 rounded-sm px-1.5 py-0.5 text-[10px] text-slate-600">
                      <Paperclip size={10} /> {a.filename}{a.size_bytes ? ` (${Math.round(a.size_bytes / 1024)} KB)` : ""}
                    </span>
                  ))}
                </div>
              )}
            </div>
            {showPoPreview && poPreview?.html_body && (
              <div className="border border-slate-200 rounded-sm overflow-hidden" data-testid="email-po-preview-container">
                <div className="bg-slate-100 border-b border-slate-200 px-3 py-1.5 text-[10px] uppercase tracking-wider text-slate-500 font-semibold">
                  Email preview — what the supplier will see (PO PDF attached)
                </div>
                <iframe title="PO email preview" srcDoc={poPreview.html_body} sandbox=""
                  className="w-full h-[320px] bg-white" data-testid="email-po-preview-iframe" />
              </div>
            )}
            <p className="text-[10px] text-slate-500">The PO PDF is attached automatically. Sent from your configured sending address (System → Email settings).</p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEmailPO(null)} disabled={emailBusy} className="rounded-sm" data-testid="email-po-cancel-btn">Cancel</Button>
            <Button data-testid="email-po-send-btn" onClick={sendEmailPO}
              disabled={emailBusy || poSelected.length === 0 || !emailForm.subject.trim()}
              className="rounded-sm bg-emerald-600 hover:bg-emerald-700 text-white">
              {emailBusy ? <Loader2 size={14} className="animate-spin mr-1.5" /> : <Mail size={14} className="mr-1.5" />}
              Send PO{poSelected.length > 1 ? ` to ${poSelected.length} recipients` : ""}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Request changes (approver push-back) */}
      <Dialog open={!!pbPO} onOpenChange={(o) => !o && setPbPO(null)}>
        <DialogContent className="rounded-sm sm:max-w-md" data-testid="po-request-changes-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-xl flex items-center gap-2">
              <Undo2 size={18} className="text-rose-600" /> Request changes — {pbPO?.order_number}
            </DialogTitle>
          </DialogHeader>
          <div className="text-xs text-slate-500">Sends the PO back to the submitter for revision or clarification. It unlocks for editing and must be resubmitted for approval. The revision number is not changed.</div>
          <Field label="What needs to change or be clarified?" required>
            <Textarea data-testid="po-request-changes-comment" rows={4} value={pbComment}
              onChange={(e) => setPbComment(e.target.value)} maxLength={2000}
              placeholder="e.g. Wrong supplier branch — this needs to go to the Tuggerah trade desk" />
          </Field>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setPbPO(null)} className="rounded-sm">Cancel</Button>
            <Button data-testid="po-request-changes-send-btn"
              disabled={wf.isPending || pbComment.trim().length < 3}
              onClick={() => wf.mutate({ id: pbPO.id, action: "request-changes", body: { comment: pbComment.trim() } })}
              className="rounded-sm bg-rose-600 hover:bg-rose-700 text-white">
              {wf.isPending ? <Loader2 size={14} className="animate-spin mr-1.5" /> : <Undo2 size={14} className="mr-1.5" />}
              Send back for changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Submit / resubmit for approval — optional message to the approver (Iter-87) */}
      <Dialog open={!!rsPO} onOpenChange={(o) => !o && setRsPO(null)}>
        <DialogContent className="rounded-sm sm:max-w-md" data-testid="po-resubmit-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-xl flex items-center gap-2">
              <ShieldCheck size={18} className="text-cyan-600" /> {rsPO?.status === "changes_requested" ? "Resubmit" : "Submit"} {rsPO?.order_number} for approval
            </DialogTitle>
          </DialogHeader>
          {rsPO?.changes_requested_comment && (
            <div className="border border-rose-200 bg-rose-50 rounded-sm px-3 py-2 text-xs text-slate-700 italic">
              “{rsPO.changes_requested_comment}” — {rsPO.changes_requested_by_name || rsPO.changes_requested_by}
            </div>
          )}
          <Field label="Message to the approver (optional)">
            <Textarea data-testid="po-resubmit-note" rows={3} value={rsNote}
              onChange={(e) => setRsNote(e.target.value)} maxLength={2000}
              placeholder={rsPO?.status === "changes_requested"
                ? "e.g. Switched to the Tuggerah branch and fixed the qty on line 2"
                : "e.g. Supplier only holds this price till Friday — can you look today?"} />
            <p className="text-[10.5px] text-slate-400 mt-1.5">Sent with the approval request — shows on the PO, in the approval trail and in the approver's notification. Internal only, never on the supplier PDF.</p>
          </Field>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setRsPO(null)} className="rounded-sm">Cancel</Button>
            <Button data-testid="po-resubmit-send-btn" disabled={wf.isPending}
              onClick={() => wf.mutate({
                id: rsPO.id, action: "submit-approval", body: { note: rsNote.trim() || null },
                resubmit: rsPO.status === "changes_requested",
                savePayload: open && editing?.id === rsPO.id
                  ? { ...editing, supplier_id: editing.supplier_id === "none" ? null : editing.supplier_id, job_id: editing.job_id === "none" ? null : editing.job_id }
                  : undefined,
              })}
              className="rounded-sm bg-cyan-600 hover:bg-cyan-700 text-white">
              {wf.isPending ? <Loader2 size={14} className="animate-spin mr-1.5" /> : <ShieldCheck size={14} className="mr-1.5" />}
              {rsPO?.status === "changes_requested" ? "Resubmit for approval" : "Submit for approval"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ---- PO Updates & chat (Trent, Nov 2026) — mirrors the deal Notes chat ----
export function POChat({ orderId }) {
  const qc = useQueryClient();
  const { user } = useAuth();
  const [text, setText] = useState("");
  const [staged, setStaged] = useState([]);
  const [sending, setSending] = useState(false);
  const fileRef = useRef(null);
  const boxRef = useRef(null);
  const { data } = useQuery({
    queryKey: ["po-chat", orderId],
    queryFn: async () => (await api.get(`/orders/${orderId}/chat`)).data,
    refetchInterval: 15000,
  });
  const msgs = data?.messages || [];
  useEffect(() => { boxRef.current?.scrollTo({ top: boxRef.current.scrollHeight }); }, [msgs.length]);

  const send = async () => {
    if (sending || (!text.trim() && staged.length === 0)) return;
    setSending(true);
    try {
      const ids = [];
      for (const f of staged) {
        const fd = new FormData();
        fd.append("file", f);
        fd.append("order_id", orderId);
        const r = await api.post("/timeline/files", fd);
        ids.push(r.data.id);
      }
      await api.post(`/orders/${orderId}/notes`, { text: text.trim(), attachment_ids: ids });
      setText(""); setStaged([]);
      qc.invalidateQueries({ queryKey: ["po-chat", orderId] });
      qc.invalidateQueries({ queryKey: ["orders-unread"] });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not send the update");
    } finally { setSending(false); }
  };
  const timeOf = (iso) => { try { return new Date(iso).toLocaleString("en-AU", { day: "numeric", month: "short", hour: "numeric", minute: "2-digit" }); } catch { return ""; } };

  return (
    <div className="flex flex-col" data-testid="po-chat-panel">
      <div ref={boxRef} className="max-h-[32vh] min-h-[90px] overflow-y-auto bg-slate-50 border border-slate-200 rounded-sm p-3 space-y-2">
        {msgs.map((m, idx) => {
          if (m.type && m.type !== "message") {
            return (
              <div key={m.id || idx} className="flex justify-center" data-testid={`po-chat-event-${m.id}`}>
                <span className={`text-[10px] font-semibold rounded-full px-3 py-1 border text-center ${m.type === "date_change" ? "bg-amber-50 border-amber-200 text-amber-800" : "bg-slate-100 border-slate-200 text-slate-600"}`}>
                  {m.text} · {m.by_name} · {timeOf(m.at)}
                </span>
              </div>
            );
          }
          const mine = m.by && m.by === user?.id;
          return (
            <div key={m.id || idx} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
              <div className={`max-w-[80%] rounded-lg px-3 py-2 ${mine ? "bg-cyan-700 text-white rounded-br-sm" : "bg-white border border-slate-200 rounded-bl-sm"}`} data-testid={`po-chat-msg-${m.id}`}>
                {!mine && <div className="text-[10px] font-bold text-cyan-700 mb-0.5">{m.by_name}</div>}
                {m.text && <div className="text-[13px] whitespace-pre-wrap leading-relaxed">{m.text}</div>}
                {(m.attachments || []).length > 0 && (
                  <div className="flex flex-wrap gap-1.5 mt-1.5">
                    {m.attachments.map((a) => (
                      <button key={a.id} type="button" onClick={() => openTimelineFile(a.id)} data-testid={`po-chat-att-${a.id}`}
                        className={`inline-flex items-center gap-1.5 text-[11px] underline rounded px-2 py-1 max-w-[220px] ${mine ? "bg-white/20 text-white" : "bg-slate-100 text-cyan-700"}`}>
                        <Paperclip size={10} className="shrink-0" /> <span className="truncate">{a.label || a.filename}</span>
                      </button>
                    ))}
                  </div>
                )}
                <div className={`text-[9px] font-mono mt-1 text-right ${mine ? "text-cyan-100/80" : "text-slate-400"}`}>{timeOf(m.at)}</div>
              </div>
            </div>
          );
        })}
        {msgs.length === 0 && <div className="text-xs text-slate-400 text-center py-6">No updates yet — supplier calls, ETA changes and delivery notes all live here.</div>}
      </div>
      {staged.length > 0 && (
        <div className="flex flex-wrap gap-1.5 pt-2">
          {staged.map((f, i) => (
            <span key={i} className="inline-flex items-center gap-1.5 text-[11px] bg-cyan-50 border border-cyan-200 text-cyan-800 rounded px-2 py-1">
              <Paperclip size={10} /> {f.name}
              <button type="button" onClick={() => setStaged(staged.filter((_, j) => j !== i))} className="hover:text-rose-600" data-testid={`po-staged-remove-${i}`}>✕</button>
            </span>
          ))}
        </div>
      )}
      <div className="flex items-end gap-2 pt-2">
        <input ref={fileRef} type="file" multiple className="hidden" data-testid="po-chat-file-input"
          onChange={(e) => { setStaged([...staged, ...Array.from(e.target.files || [])].slice(0, 10)); e.target.value = ""; }} />
        <button type="button" onClick={() => fileRef.current?.click()} title="Attach any file"
          className="w-9 h-9 rounded-full border border-slate-300 text-slate-500 hover:text-cyan-700 hover:border-cyan-400 flex items-center justify-center shrink-0" data-testid="po-chat-attach-btn">
          <Paperclip size={15} />
        </button>
        <Textarea rows={1} value={text} onChange={(e) => setText(e.target.value)} placeholder="Log a supplier update, delay reason or note…"
          onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
          className="rounded-2xl text-sm min-h-[38px] max-h-28 resize-none py-2" data-testid="po-chat-input" />
        <button type="button" onClick={send} disabled={sending || (!text.trim() && staged.length === 0)}
          className="w-10 h-10 rounded-full bg-cyan-600 hover:bg-cyan-700 disabled:opacity-40 text-white flex items-center justify-center shrink-0" data-testid="po-chat-send-btn">
          {sending ? <Loader2 size={15} className="animate-spin" /> : <Send size={15} />}
        </button>
      </div>
    </div>
  );
}
